import React, { useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, Users, Download, Trash2, Loader2, BellRing, Send, Lightbulb, RefreshCw, Check, X as XIcon, RotateCcw, VolumeX, Volume2, Clock, Sunrise, Calendar, Newspaper, Save, Sparkles, ImagePlus, MapPin } from "lucide-react";
import AnalyticsPanel from "@/components/AnalyticsPanel";

function toCsv(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
}

function download(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminPanel() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [pushKind, setPushKind] = useState("admin");
  const [pushBusy, setPushBusy] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [sugBusy, setSugBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [muted, setMuted] = useState([]);
  const [mutedBusy, setMutedBusy] = useState(false);
  const [dailyBusy, setDailyBusy] = useState(false);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [eventsBusy, setEventsBusy] = useState(false);
  const [community, setCommunity] = useState([]);
  const [communityBusy, setCommunityBusy] = useState(false);
  const [communityStatus, setCommunityStatus] = useState("pending");
  const [businesses, setBusinesses] = useState([]);
  const [businessesBusy, setBusinessesBusy] = useState(false);
  const [businessesStatus, setBusinessesStatus] = useState("pending");

  if (!user || user.role !== "admin") return null;

  const loadSuggestions = async () => {
    queueMicrotask(() => setSugBusy(true));
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await http.get("/admin/suggestions", { params });
      queueMicrotask(() => setSugs(data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load suggestions");
    } finally {
      queueMicrotask(() => setSugBusy(false));
    }
  };

  const updateSug = async (sid, patch) => {
    try {
      const { data } = await http.patch(`/admin/suggestions/${sid}`, patch);
      queueMicrotask(() => setSugs((arr) => arr.map((s) => (s.id === sid ? data : s))));
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const deleteSug = async (sid) => {
    if (!window.confirm("Delete this suggestion?")) return;
    try {
      await http.delete(`/admin/suggestions/${sid}`);
      queueMicrotask(() => setSugs((arr) => arr.filter((s) => s.id !== sid)));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const loadCommunity = async () => {
    setCommunityBusy(true);
    try {
      const params = communityStatus ? { status: communityStatus } : {};
      const { data } = await http.get("/admin/community/submissions", { params });
      setCommunity(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load submissions");
    } finally {
      setCommunityBusy(false);
    }
  };

  const updateCommunity = async (sid, patch) => {
    try {
      const { data } = await http.patch(`/admin/community/submissions/${sid}`, patch);
      setCommunity((arr) => arr.map((s) => (s.id === sid ? data : s)));
      toast.success(patch.status ? `Marked ${patch.status}` : "Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const deleteCommunity = async (sid) => {
    if (!window.confirm("Delete this community submission?")) return;
    try {
      await http.delete(`/admin/community/submissions/${sid}`);
      setCommunity((arr) => arr.filter((s) => s.id !== sid));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const loadBusinesses = async () => {
    setBusinessesBusy(true);
    try {
      const params = businessesStatus ? { status: businessesStatus } : {};
      const { data } = await http.get("/admin/local-businesses", { params });
      setBusinesses(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load businesses");
    } finally {
      setBusinessesBusy(false);
    }
  };

  const updateBusiness = async (sid, patch) => {
    try {
      const { data } = await http.patch(`/admin/local-businesses/${sid}`, patch);
      setBusinesses((arr) => arr.map((s) => (s.id === sid ? data : s)));
      toast.success(patch.status ? `Marked ${patch.status}` : "Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const deleteBusiness = async (sid) => {
    if (!window.confirm("Delete this business submission?")) return;
    try {
      await http.delete(`/admin/local-businesses/${sid}`);
      setBusinesses((arr) => arr.filter((s) => s.id !== sid));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const loadUsers = async () => {
    queueMicrotask(() => setBusy(true));
    try {
      const { data } = await http.get("/admin/users", { params: q ? { q } : {} });
      queueMicrotask(() => setRows(data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load users");
    } finally { queueMicrotask(() => setBusy(false)); }
  };

  const exportCsv = () => {
    if (!rows.length) { toast("Load the user list first"); return; }
    download(`sjds-users-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows));
  };

  const purge = async (scope, label) => {
    if (!window.confirm(`Wipe ALL ${label}? This cannot be undone.`)) return;
    queueMicrotask(() => setPurgeBusy(true));
    try {
      const { data } = await http.post("/admin/purge", { scope });
      toast.success(`Wiped ${label}: ${JSON.stringify(data.deleted)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Purge failed");
    } finally { queueMicrotask(() => setPurgeBusy(false)); }
  };

  const sendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) {
      toast("Title and body are required");
      return;
    }
    queueMicrotask(() => setPushBusy(true));
    try {
      const { data } = await http.post("/admin/push/broadcast", {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        kind: pushKind,
        url: "/#alerts",
      });
      toast.success(`Push sent ✓  ${data.sent} delivered · ${data.failed} failed · ${data.skipped_opted_out} opted out`);
      queueMicrotask(() => { setPushTitle(""); setPushBody(""); });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Push send failed");
    } finally { queueMicrotask(() => setPushBusy(false)); }
  };

  const sendTestPush = async () => {
    queueMicrotask(() => setPushBusy(true));
    try {
      const { data } = await http.post("/push/test", {});
      toast.success(`Test push: ${data.sent} delivered, ${data.failed} failed (your devices only)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test push failed");
    } finally { queueMicrotask(() => setPushBusy(false)); }
  };

  const resetAlertHistory = async () => {
    if (!window.confirm(
      "Wipe the auto-watcher 'already seen' logs for earthquakes, hurricanes & tsunamis?\n\n" +
      "On the next poll, ALL currently active events will re-broadcast a push notification."
    )) return;
    queueMicrotask(() => setResetBusy(true));
    try {
      const { data } = await http.post("/admin/alerts/reset", { scopes: ["earthquake", "hurricane", "tsunami"] });
      toast.success(`Watchers reset ✓  eq ${data.deleted?.earthquake ?? 0} · hu ${data.deleted?.hurricane ?? 0} · ts ${data.deleted?.tsunami ?? 0}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reset failed");
    } finally { queueMicrotask(() => setResetBusy(false)); }
  };

  const loadMuted = async () => {
    queueMicrotask(() => setMutedBusy(true));
    try {
      const { data } = await http.get("/admin/chat/muted");
      queueMicrotask(() => setMuted(data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load muted users");
    } finally { queueMicrotask(() => setMutedBusy(false)); }
  };

  const unmuteUser = async (uid) => {
    try {
      await http.post(`/admin/users/${uid}/unmute`);
      queueMicrotask(() => setMuted((arr) => arr.filter((u) => u.id !== uid)));
      toast.success("User un-muted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Un-mute failed");
    }
  };

  const muteUserFromRoster = async (u) => {
    const mins = window.prompt(`Mute @${u.username || u.email} for how many minutes? (1 – ${60 * 24 * 30})`, "60");    if (!mins) return;
    const minutes = parseInt(mins, 10);
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 60 * 24 * 30) {
      toast.error("Invalid duration");
      return;
    }
    const reason = window.prompt("Optional reason (visible to admins only):", "") || "";
    try {
      await http.post(`/admin/users/${u.id}/mute`, { minutes, reason });
      toast.success(`Muted @${u.username || u.email} for ${minutes} min`);
      loadMuted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Mute failed");
    }
  };

  return (
    <>
      <AnalyticsPanel />
      <section data-testid="admin-panel" className="clay p-5 md:p-6 border-2 border-[color:var(--coral)]/30 mt-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-[color:var(--coral)]" />
        <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>{t("admin_tools")}</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-[color:var(--coral)] font-bold">{t("admin_restricted")}</span>
      </div>

      {/* User roster */}
      <div className="space-y-3">
        <p className="eyebrow flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{t("admin_roster")}</p>
        <div className="flex gap-2 flex-wrap">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin_filter")} className="rounded-2xl max-w-xs" data-testid="admin-user-search" />
          <Button onClick={loadUsers} disabled={busy} data-testid="admin-load-users" className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Users className="w-4 h-4 mr-1.5" />} {t("admin_load_users")}
          </Button>
          <Button onClick={exportCsv} variant="outline" disabled={!rows.length} data-testid="admin-export-csv" className="pill">
            <Download className="w-4 h-4 mr-1.5" /> {t("admin_export_csv")} ({rows.length})
          </Button>
        </div>
        {rows.length > 0 && (
          <div className="max-h-64 overflow-auto rounded-2xl border border-[color:var(--ink-2)]/10 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-[color:var(--sand-2)] sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">@user</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Joined</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-t border-[color:var(--ink-2)]/10">
                    <td className="px-3 py-1.5 font-mono">{u.email}</td>
                    <td className="px-3 py-1.5">{u.name}</td>
                    <td className="px-3 py-1.5 text-[color:var(--ink-2)]">@{u.username}</td>
                    <td className="px-3 py-1.5">{u.role === "admin" ? <span className="text-[color:var(--coral)] font-semibold">admin</span> : "user"}</td>
                    <td className="px-3 py-1.5 text-[color:var(--ink-2)]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      {u.role !== "admin" && (
                        <button
                          onClick={() => muteUserFromRoster(u)}
                          data-testid={`admin-mute-row-${u.id}`}
                          title="Mute from chat"
                          className="text-[color:var(--ink-2)] hover:text-[color:var(--emergency)] inline-flex items-center gap-1"
                        >
                          <VolumeX className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Push broadcast */}
      <div className="space-y-2" data-testid="admin-push-section">
        <p className="eyebrow flex items-center gap-1.5 text-[color:var(--coral)]"><BellRing className="w-3.5 h-3.5" />Send push notification</p>
        <p className="text-xs text-[color:var(--ink-2)]">
          Broadcasts to every subscribed device whose owner has opted in for this category.
        </p>
        <div className="grid sm:grid-cols-[1fr_180px] gap-2">
          <Input
            value={pushTitle}
            onChange={(e) => setPushTitle(e.target.value)}
            placeholder="Title (max 80 chars)"
            maxLength={80}
            className="rounded-2xl"
            data-testid="push-title"
          />
          <select
            value={pushKind}
            onChange={(e) => setPushKind(e.target.value)}
            className="rounded-2xl border border-[color:var(--ink-2)]/20 px-3 text-sm bg-white"
            data-testid="push-kind"
          >
            <option value="admin">Admin announcement</option>
            <option value="earthquakes">Earthquake</option>
            <option value="storms">Storm / weather</option>
            <option value="chat">Chat / community</option>
          </select>
        </div>
        <Textarea
          value={pushBody}
          onChange={(e) => setPushBody(e.target.value)}
          placeholder="Notification body (max 240 chars)"
          maxLength={240}
          rows={2}
          className="rounded-2xl"
          data-testid="push-body"
        />
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={sendPush}
            disabled={pushBusy || !pushTitle.trim() || !pushBody.trim()}
            data-testid="push-send"
            className="pill bg-[color:var(--coral)] text-white hover:opacity-90"
          >
            {pushBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send to all subscribers
          </Button>
          <Button
            onClick={sendTestPush}
            disabled={pushBusy}
            variant="outline"
            data-testid="push-send-test"
            className="pill"
          >
            <BellRing className="w-4 h-4 mr-1.5" />
            Test on my devices
          </Button>
        </div>
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Community suggestions inbox */}
      <div className="space-y-3" data-testid="admin-suggestions-section">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="eyebrow flex items-center gap-1.5 text-[color:var(--coral)]">
            <Lightbulb className="w-3.5 h-3.5" /> Community suggestions
          </p>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ml-auto rounded-2xl border border-[color:var(--ink-2)]/20 px-3 py-1.5 text-xs bg-white"
            data-testid="admin-sug-filter"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In progress</option>
            <option value="done">Done</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button
            onClick={loadSuggestions}
            disabled={sugBusy}
            variant="outline"
            className="pill"
            data-testid="admin-sug-load"
          >
            {sugBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Load
          </Button>
        </div>

        {sugs.length === 0 ? (
          <p className="text-xs text-[color:var(--ink-2)] italic">
            {sugBusy ? "Loading…" : "Click Load to see community suggestions."}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-sug-list">
            {sugs.map((s) => (
              <li key={s.id} className="rounded-2xl border border-[color:var(--ink-2)]/10 p-3 bg-white">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-semibold text-sm">{s.title}</p>
                    <p className="text-[11px] text-[color:var(--ink-2)]">
                      <strong className="font-mono">{s.user_email}</strong> · {s.category} · {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <select
                    value={s.status}
                    onChange={(e) => updateSug(s.id, { status: e.target.value })}
                    className="rounded-xl border border-[color:var(--ink-2)]/20 px-2 py-1 text-xs bg-white"
                    data-testid={`admin-sug-status-${s.id}`}
                  >
                    <option value="new">new</option>
                    <option value="planned">planned</option>
                    <option value="in_progress">in_progress</option>
                    <option value="done">done</option>
                    <option value="rejected">rejected</option>
                  </select>
                </div>
                <p className="text-xs text-[color:var(--ink)] mt-2 whitespace-pre-wrap">{s.body}</p>
                <div className="mt-2 flex gap-2 items-start flex-wrap">
                  <Input
                    defaultValue={s.admin_note}
                    placeholder="Admin reply (visible to the user)"
                    className="rounded-2xl flex-1 min-w-[180px] text-sm"
                    onBlur={(e) => {
                      if (e.target.value !== s.admin_note) updateSug(s.id, { admin_note: e.target.value });
                    }}
                    data-testid={`admin-sug-note-${s.id}`}
                  />
                  <Button
                    variant="outline"
                    onClick={() => deleteSug(s.id)}
                    className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]"
                    data-testid={`admin-sug-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Community Board — history submissions moderation */}
      <div className="space-y-3" data-testid="admin-community-section">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="eyebrow flex items-center gap-1.5 text-[color:var(--coral)]">
            <Lightbulb className="w-3.5 h-3.5" /> Community history submissions
          </p>
          <select
            value={communityStatus}
            onChange={(e) => setCommunityStatus(e.target.value)}
            className="ml-auto rounded-2xl border border-[color:var(--ink-2)]/20 px-3 py-1.5 text-xs bg-white"
            data-testid="admin-community-filter"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
          <Button
            onClick={loadCommunity}
            disabled={communityBusy}
            variant="outline"
            className="pill"
            data-testid="admin-community-load"
          >
            {communityBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Load
          </Button>
        </div>

        {community.length === 0 ? (
          <p className="text-xs text-[color:var(--ink-2)] italic">
            {communityBusy ? "Loading…" : "Click Load to see community submissions."}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-community-list">
            {community.map((s) => (
              <li key={s.id} className="rounded-2xl border border-[color:var(--ink-2)]/10 p-3 bg-white">
                <div className="flex items-start gap-3 flex-wrap">
                  {s.photo_file_id && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/community/photos/${s.photo_file_id}`}
                      alt=""
                      loading="lazy"
                      className="w-20 h-20 rounded-xl object-cover bg-[color:var(--sand-2)]"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-semibold text-sm">{s.title}</p>
                    <p className="text-[11px] text-[color:var(--ink-2)]">
                      <strong className="font-mono">{s.user_email}</strong>
                      {s.year ? ` · ${s.year}` : ""}{s.location ? ` · ${s.location}` : ""}
                      {s.created_at ? ` · ${new Date(s.created_at).toLocaleString()}` : ""}
                    </p>
                    <p className="text-xs text-[color:var(--ink)] mt-2 whitespace-pre-wrap">{s.story}</p>
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                    style={{
                      background: s.status === "approved" ? "#D7F2DA" : s.status === "rejected" ? "#F5D5D2" : "var(--sand-2)",
                      color: s.status === "approved" ? "#1E7B36" : s.status === "rejected" ? "#8C2017" : "var(--ink-2)",
                    }}
                  >
                    {s.status}
                  </span>
                </div>
                <div className="mt-2 flex gap-2 items-start flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => updateCommunity(s.id, { status: "approved" })}
                    className="pill border-emerald-500/40 text-emerald-700"
                    data-testid={`admin-community-approve-${s.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateCommunity(s.id, { status: "rejected" })}
                    className="pill border-[color:var(--ink-2)]/40"
                    data-testid={`admin-community-reject-${s.id}`}
                  >
                    <XIcon className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Input
                    defaultValue={s.admin_note}
                    placeholder="Internal note (not shown publicly)"
                    className="rounded-2xl flex-1 min-w-[180px] text-sm"
                    onBlur={(e) => {
                      if (e.target.value !== s.admin_note) updateCommunity(s.id, { admin_note: e.target.value });
                    }}
                    data-testid={`admin-community-note-${s.id}`}
                  />
                  <Button
                    variant="outline"
                    onClick={() => deleteCommunity(s.id)}
                    className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]"
                    data-testid={`admin-community-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Local businesses — user-submitted directory websites moderation */}
      <div className="space-y-3" data-testid="admin-businesses-section">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="eyebrow flex items-center gap-1.5 text-[color:var(--teal)]">
            <MapPin className="w-3.5 h-3.5" /> Local-business submissions
          </p>
          <select
            value={businessesStatus}
            onChange={(e) => setBusinessesStatus(e.target.value)}
            className="ml-auto rounded-2xl border border-[color:var(--ink-2)]/20 px-3 py-1.5 text-xs bg-white"
            data-testid="admin-businesses-filter"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
          <Button
            onClick={loadBusinesses}
            disabled={businessesBusy}
            variant="outline"
            className="pill"
            data-testid="admin-businesses-load"
          >
            {businessesBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Load
          </Button>
        </div>

        {businesses.length === 0 ? (
          <p className="text-xs text-[color:var(--ink-2)] italic">
            {businessesBusy ? "Loading…" : "Click Load to see business submissions."}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-businesses-list">
            {businesses.map((b) => (
              <li key={b.id} className="rounded-2xl border border-[color:var(--ink-2)]/10 p-3 bg-white">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-semibold text-sm">
                      <a href={`https://www.${b.domain}`} target="_blank" rel="noopener noreferrer" className="text-[color:var(--navy)] hover:text-[color:var(--coral)]">
                        {b.name || b.domain}
                      </a>
                    </p>
                    <p className="text-[11px] font-mono text-[color:var(--ink-2)]">www.{b.domain}</p>
                    <p className="text-[11px] text-[color:var(--ink-2)] mt-0.5">
                      by <strong>{b.user_email}</strong>{b.category ? ` · ${b.category}` : ""}{b.created_at ? ` · ${new Date(b.created_at).toLocaleString()}` : ""}
                    </p>
                    {b.description && <p className="text-xs text-[color:var(--ink)] mt-2 whitespace-pre-wrap">{b.description}</p>}
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                    style={{
                      background: b.status === "approved" ? "#D7F2DA" : b.status === "rejected" ? "#F5D5D2" : "var(--sand-2)",
                      color: b.status === "approved" ? "#1E7B36" : b.status === "rejected" ? "#8C2017" : "var(--ink-2)",
                    }}
                  >
                    {b.status}
                  </span>
                </div>
                <div className="mt-2 flex gap-2 items-start flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => updateBusiness(b.id, { status: "approved" })}
                    className="pill border-emerald-500/40 text-emerald-700"
                    data-testid={`admin-businesses-approve-${b.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateBusiness(b.id, { status: "rejected" })}
                    className="pill border-[color:var(--ink-2)]/40"
                    data-testid={`admin-businesses-reject-${b.id}`}
                  >
                    <XIcon className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => deleteBusiness(b.id)}
                    className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]"
                    data-testid={`admin-businesses-delete-${b.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Blog composer + list */}
      <BlogComposer />

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Events moderation */}
      <div className="space-y-3" data-testid="admin-events-section">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="eyebrow flex items-center gap-1.5 text-[color:var(--coral)]">
            <Calendar className="w-3.5 h-3.5" /> Event proposals
          </p>
          <Button
            onClick={async () => {
              queueMicrotask(() => setEventsBusy(true));
              try {
                const { data } = await http.get("/admin/events?status=pending");
                queueMicrotask(() => setPendingEvents(data));
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Load failed");
              } finally { queueMicrotask(() => setEventsBusy(false)); }
            }}
            disabled={eventsBusy}
            variant="outline"
            className="pill ml-auto"
            data-testid="admin-events-load"
          >
            {eventsBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Load pending
          </Button>
        </div>
        {pendingEvents.length === 0 ? (
          <p className="text-xs text-[color:var(--ink-2)] italic">
            {eventsBusy ? "Loading…" : "No pending events."}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-events-pending-list">
            {pendingEvents.map((ev) => (
              <li key={ev.id} className="rounded-2xl border border-[color:var(--coral)]/30 p-3 bg-white flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-semibold text-sm">{ev.title}</p>
                  <p className="text-[11px] text-[color:var(--ink-2)] mt-0.5">
                    {ev.category.toUpperCase()} · {new Date(ev.start_at).toLocaleString()} · {ev.location || "(no location)"}
                  </p>
                  <p className="text-xs text-[color:var(--ink)] mt-1">{ev.description || <em className="text-[color:var(--ink-2)]">(no description)</em>}</p>
                  <p className="text-[11px] text-[color:var(--ink-2)] mt-1">
                    from {ev.user_email}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    onClick={async () => {
                      try {
                        await http.patch(`/admin/events/${ev.id}`, { status: "approved" });
                        queueMicrotask(() => setPendingEvents((arr) => arr.filter((e) => e.id !== ev.id)));
                        toast.success("Event approved");
                      } catch (e) { toast.error("Approve failed"); }
                    }}
                    data-testid={`admin-event-approve-${ev.id}`}
                    className="pill bg-[color:var(--teal)] text-white hover:bg-[color:var(--teal)]/90"
                  >
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        await http.patch(`/admin/events/${ev.id}`, { status: "rejected" });
                        queueMicrotask(() => setPendingEvents((arr) => arr.filter((e) => e.id !== ev.id)));
                        toast.success("Event rejected");
                      } catch (e) { toast.error("Reject failed"); }
                    }}
                    data-testid={`admin-event-reject-${ev.id}`}
                    variant="outline"
                    className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]"
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Daily surf push — manual trigger */}
      <div className="space-y-2" data-testid="admin-daily-surf-section">
        <p className="eyebrow flex items-center gap-1.5 text-[color:var(--coral)]">
          <Sunrise className="w-3.5 h-3.5" /> Daily surf push
        </p>
        <p className="text-xs text-[color:var(--ink-2)]">
          Fires automatically every day at 6 am Managua time with today&apos;s
          Playa Maderas conditions. Use the button below to trigger a fresh
          broadcast right now (useful for testing or a special swell day).
        </p>
        <Button
          onClick={async () => {
            queueMicrotask(() => setDailyBusy(true));
            try {
              const { data } = await http.post("/admin/daily-surf/fire-now");
              toast.success(`Daily surf push fired for ${data.date} ✓`);
            } catch (e) {
              toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Daily surf push failed");
            } finally { queueMicrotask(() => setDailyBusy(false)); }
          }}
          disabled={dailyBusy}
          variant="outline"
          data-testid="admin-daily-surf-now"
          className="pill border-[color:var(--coral)]/40 text-[color:var(--coral)] hover:bg-[color:var(--coral)] hover:text-white"
        >
          {dailyBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sunrise className="w-4 h-4 mr-1.5" />}
          Fire today&apos;s daily surf push
        </Button>
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Auto-alert watcher reset */}
      <div className="space-y-2" data-testid="admin-alerts-reset-section">
        <p className="eyebrow flex items-center gap-1.5 text-[color:var(--navy)]">
          <RotateCcw className="w-3.5 h-3.5" /> Auto-alert history
        </p>
        <p className="text-xs text-[color:var(--ink-2)]">
          Background watchers track which earthquakes, hurricanes &amp; tsunamis have already pushed.
          Wiping these logs forces the next poll to re-broadcast any still-active events.
        </p>
        <Button
          onClick={resetAlertHistory}
          disabled={resetBusy}
          variant="outline"
          data-testid="admin-reset-alerts"
          className="pill border-[color:var(--navy)]/40 text-[color:var(--navy)] hover:bg-[color:var(--navy)] hover:text-white"
        >
          {resetBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
          Wipe alert history (re-broadcast active events)
        </Button>
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Chat moderation: muted users */}
      <div className="space-y-3" data-testid="admin-muted-section">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="eyebrow flex items-center gap-1.5 text-[color:var(--emergency)]">
            <VolumeX className="w-3.5 h-3.5" /> Muted users
          </p>
          <Button
            onClick={loadMuted}
            disabled={mutedBusy}
            variant="outline"
            className="pill ml-auto"
            data-testid="admin-muted-load"
          >
            {mutedBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Load
          </Button>
        </div>
        <p className="text-xs text-[color:var(--ink-2)]">
          Muted users can read chat but their posts are silently rejected until the timer expires.
          To mute a new user, hover their row above (or use the speaker icon next to any chat message).
        </p>
        {muted.length === 0 ? (
          <p className="text-xs text-[color:var(--ink-2)] italic">
            {mutedBusy ? "Loading…" : "No users are currently muted."}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-muted-list">
            {muted.map((m) => {
              const until = m.muted_until ? new Date(m.muted_until) : null;
              const minsLeft = until ? Math.max(0, Math.round((until.getTime() - Date.now()) / 60000)) : 0;
              return (
                <li key={m.id} className="rounded-2xl border border-[color:var(--emergency)]/30 p-3 bg-white flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-semibold text-sm">@{m.username || m.email}</p>
                    <p className="text-[11px] text-[color:var(--ink-2)] flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {minsLeft} min left · expires {until?.toLocaleString() || "—"}
                    </p>
                    {m.muted_reason && (
                      <p className="text-[11px] text-[color:var(--ink-2)] italic mt-0.5">&ldquo;{m.muted_reason}&rdquo;</p>
                    )}
                  </div>
                  <Button
                    onClick={() => unmuteUser(m.id)}
                    variant="outline"
                    className="pill border-[color:var(--teal)]/40 text-[color:var(--teal)]"
                    data-testid={`admin-unmute-${m.id}`}
                  >
                    <Volume2 className="w-4 h-4 mr-1.5" /> Un-mute
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <hr className="my-5 border-[color:var(--ink-2)]/10" />

      {/* Purge test data */}
      <div className="space-y-2">
        <p className="eyebrow flex items-center gap-1.5 text-[color:var(--emergency)]"><Trash2 className="w-3.5 h-3.5" />{t("admin_purge_title")}</p>
        <p className="text-xs text-[color:var(--ink-2)]">{t("admin_purge_hint")}</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => purge("alerts", t("admin_purge_alerts"))} disabled={purgeBusy} variant="outline" data-testid="admin-purge-alerts" className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]">{t("admin_purge_alerts")}</Button>
          <Button onClick={() => purge("chat", t("admin_purge_chat"))} disabled={purgeBusy} variant="outline" data-testid="admin-purge-chat" className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]">{t("admin_purge_chat")}</Button>
          <Button onClick={() => purge("plans", t("admin_purge_plans"))} disabled={purgeBusy} variant="outline" data-testid="admin-purge-plans" className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]">{t("admin_purge_plans")}</Button>
          <Button onClick={() => purge("sessions", t("admin_purge_sessions"))} disabled={purgeBusy} variant="outline" data-testid="admin-purge-sessions" className="pill border-[color:var(--emergency)]/40 text-[color:var(--emergency)]">{t("admin_purge_sessions")}</Button>
        </div>
      </div>
    </section>
    </>
  );
}

/**
 * Inline blog composer / editor for admins. Simple: title + optional slug +
 * excerpt + markdown body + cover image URL + publish toggle. Lists existing
 * posts so the admin can quickly edit / delete.
 */
function BlogComposer() {
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", slug: "", excerpt: "", body_md: "", category: "guide", tags: "",
    cover_image_url: "", is_published: true,
  });
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = React.useRef(null);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await http.post("/admin/blog/generate", {
        topic: topic.trim() || undefined,
        length_words: 180,
      });
      setForm((f) => ({
        ...f,
        title: data.title || "",
        slug: data.slug || "",
        excerpt: data.excerpt || "",
        body_md: data.body_md || "",
        category: data.category || "guide",
        tags: (data.tags || []).join(", "),
        is_published: data.is_published !== false,
        // keep existing cover_image_url — admin uploads it separately
      }));
      toast.success("Draft ready — review, add a cover photo, and publish.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is too large (5 MB max).");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/blog/cover", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Persist the ABSOLUTE URL so /blog/{slug} can render the image
      // regardless of which host the frontend is served from.
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const absolute = data.url?.startsWith("http") ? data.url : `${backend}${data.url}`;
      setForm((f) => ({ ...f, cover_image_url: absolute }));
      toast.success("Cover photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const generateCover = async () => {
    const title = form.title.trim();
    if (title.length < 3) {
      toast.error("Enter a post title first (or click Generate with AI).");
      return;
    }
    setUploadingCover(true);
    try {
      const { data } = await http.post("/admin/blog/cover/ai", { title });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const absolute = data.url?.startsWith("http") ? data.url : `${backend}${data.url}`;
      setForm((f) => ({ ...f, cover_image_url: absolute }));
      toast.success("AI cover ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "AI cover generation failed");
    } finally {
      setUploadingCover(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await http.get("/admin/blog");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Load failed");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setEditing(null);
    setForm({ title: "", slug: "", excerpt: "", body_md: "", category: "guide", tags: "", cover_image_url: "", is_published: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.body_md.trim().length < 10) {
      toast.error("Title and body (min 10 chars) are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        body_md: form.body_md,
        category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        cover_image_url: form.cover_image_url || null,
        is_published: form.is_published,
      };
      if (editing) {
        await http.patch(`/admin/blog/${editing}`, payload);
        toast.success("Post updated");
      } else {
        await http.post("/admin/blog", payload);
        toast.success("Post published");
      }
      reset();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const edit = async (post) => {
    // Fetch full body via public endpoint (published) or admin list gives excerpt only
    try {
      const { data } = await http.get(`/blog/${post.slug}`);
      setEditing(post.id);
      setForm({
        title: data.title, slug: data.slug, excerpt: data.excerpt,
        body_md: data.body_md, category: data.category,
        tags: (data.tags || []).join(", "),
        cover_image_url: data.cover_image_url || "",
        is_published: post.is_published !== false,
      });
    } catch (_e) {
      // fallback: unpublished posts aren't readable via /blog/{slug} — copy from admin data
      setEditing(post.id);
      setForm((f) => ({ ...f, title: post.title, slug: post.slug, excerpt: post.excerpt, category: post.category, tags: (post.tags || []).join(", "), cover_image_url: post.cover_image_url || "", is_published: post.is_published !== false, body_md: "" }));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await http.delete(`/admin/blog/${id}`);
      queueMicrotask(() => setPosts((arr) => arr.filter((p) => p.id !== id)));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-blog-section">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="eyebrow flex items-center gap-1.5 text-[color:var(--coral)]">
          <Newspaper className="w-3.5 h-3.5" /> Blog editor
        </p>
        <Button onClick={load} disabled={busy} variant="outline" className="pill ml-auto" data-testid="admin-blog-load">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Load posts
        </Button>
      </div>

      <form onSubmit={submit} className="grid gap-2" data-testid="admin-blog-form">
        {/* AI generate row */}
        <div className="rounded-2xl bg-[color:var(--sand-2)] border border-[color:var(--coral)]/25 p-3 flex items-center gap-2 flex-wrap" data-testid="admin-blog-ai-row">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[color:var(--coral)] font-bold">
            <Sparkles className="w-3.5 h-3.5" /> AI draft
          </div>
          <Input
            data-testid="admin-blog-topic"
            placeholder="Topic (optional — leave blank to let AI pick one)"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="flex-1 min-w-[220px] rounded-2xl bg-white"
          />
          <Button
            type="button"
            onClick={generate}
            disabled={generating}
            data-testid="admin-blog-generate"
            className="pill bg-[color:var(--coral)] text-white hover:opacity-90"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {generating ? "Writing…" : "Generate with AI"}
          </Button>
        </div>

        <Input
          data-testid="admin-blog-title"
          placeholder="Post title (e.g. Best Time to Surf Playa Maderas)"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className="rounded-2xl"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            data-testid="admin-blog-slug"
            placeholder="URL slug (auto if blank)"
            value={form.slug}
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
            className="rounded-2xl"
          />
          <Input
            placeholder="Category (guide, food, surf, event…)"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="rounded-2xl"
          />
        </div>
        <Input
          data-testid="admin-blog-excerpt"
          placeholder="Excerpt / meta description (max 300 chars — for Google & social share)"
          value={form.excerpt}
          onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
          className="rounded-2xl"
        />

        {/* Cover image: URL field + upload button + thumbnail preview */}
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Cover image URL (used as OG image on share)"
            value={form.cover_image_url}
            onChange={(e) => setForm((f) => ({ ...f, cover_image_url: e.target.value }))}
            className="rounded-2xl flex-1 min-w-[220px]"
            data-testid="admin-blog-cover-url"
          />
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={uploadCover}
            className="hidden"
            data-testid="admin-blog-cover-input"
          />
          <Button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            disabled={uploadingCover}
            variant="outline"
            className="pill"
            data-testid="admin-blog-cover-btn"
          >
            {uploadingCover ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />}
            {uploadingCover ? "Working…" : "Upload photo"}
          </Button>
          <Button
            type="button"
            onClick={generateCover}
            disabled={uploadingCover || !form.title.trim()}
            className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]"
            data-testid="admin-blog-cover-ai-btn"
            title={form.title.trim() ? "Generate a cover with AI from the post title" : "Enter a title first"}
          >
            {uploadingCover ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            AI cover
          </Button>
          {form.cover_image_url && (
            <img
              src={form.cover_image_url}
              alt="cover preview"
              className="w-14 h-14 rounded-xl object-cover border border-[color:var(--ink-2)]/20"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="admin-blog-cover-preview"
            />
          )}
        </div>

        <Input
          placeholder="Tags (comma-separated, e.g. playa-maderas, surf, guide)"
          value={form.tags}
          onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          className="rounded-2xl"
        />
        <Textarea
          data-testid="admin-blog-body"
          placeholder="Body — supports Markdown (# heading, **bold**, - list, [link](url))"
          value={form.body_md}
          onChange={(e) => setForm((f) => ({ ...f, body_md: e.target.value }))}
          rows={10}
          className="rounded-2xl font-mono text-sm"
        />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
            className="w-4 h-4 accent-[color:var(--coral)]"
          />
          Publish immediately (uncheck to save as draft)
        </label>
        <div className="flex gap-2">
          <Button type="submit" disabled={saving} data-testid="admin-blog-save" className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {editing ? "Update post" : "Publish post"}
          </Button>
          {editing && (
            <Button type="button" onClick={reset} variant="outline" className="pill">
              Cancel edit
            </Button>
          )}
        </div>
      </form>

      {posts.length > 0 && (
        <ul className="space-y-2 mt-4" data-testid="admin-blog-list">
          {posts.map((p) => (
            <li key={p.id} className="rounded-2xl border border-[color:var(--ink-2)]/10 p-3 bg-white flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{p.title}</p>
                <p className="text-[11px] text-[color:var(--ink-2)]">
                  /{p.slug} · {p.category}{p.is_published === false ? " · DRAFT" : ""}
                </p>
              </div>
              <Button type="button" onClick={() => edit(p)} variant="outline" className="pill text-xs" data-testid={`admin-blog-edit-${p.id}`}>Edit</Button>
              <Button type="button" onClick={() => del(p.id)} variant="outline" className="pill text-xs border-[color:var(--emergency)]/40 text-[color:var(--emergency)]" data-testid={`admin-blog-delete-${p.id}`}>Delete</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
